import { useEffect, useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ListingSearchCriteria } from '@components/layouts/Truva/ListingSearchDeprecated/utils';
import { BuildingAmenityDescription, AllAmenities, ListingSearchFilterTypes } from '@root/types';
import Checkbox from '@components/shared/forms/RawCheckbox';
import { useDispatch } from 'react-redux';
import actions from '@store/actions/listingSearchPage';
import s from '../Filters.module.less';

type Props = {
  onChange: (criteria: ListingSearchCriteria) => void;
  criteria: ListingSearchCriteria;
  type: ListingSearchFilterTypes;
};

type ListingAmenityDescription =
  | 'Turnstiles'
  | 'Furnished'
  | 'Key Card Space Entry'
  | 'Passenger Elevator Count'
  | 'Service Elevator Count';

type AmenityDescription = BuildingAmenityDescription | ListingAmenityDescription;
type AmenityObject = {
  key: keyof AllAmenities;
  untranslatedLabel: AmenityDescription;
};

// If you are adding amenities,
const SEARCHABLE_AMENITIES: AmenityObject[] = [
  { key: 'bikeStorageAvailable', untranslatedLabel: 'Bike Storage Available' },
  { key: 'lobbyAttendant', untranslatedLabel: 'Lobby Attendant' },
  { key: 'communalOutdoorSpace', untranslatedLabel: 'Communal Outdoor Space' },
  { key: 'twentyFourSevenAccess', untranslatedLabel: '24/7 Access' },
  { key: 'gymAvailable', untranslatedLabel: 'Gym Available' },
  { key: 'barAvailable', untranslatedLabel: 'Bar Available' },
  { key: 'cafeAvailable', untranslatedLabel: 'Cafe Available' },
  { key: 'communalLobbySpace', untranslatedLabel: 'Communal Lobby Space' },
  { key: 'communalRoofAccess', untranslatedLabel: 'Communal Roof Access' },
  { key: 'foodServiceAvailable', untranslatedLabel: 'Food Service Available' },
  { key: 'generalStoreAvailable', untranslatedLabel: 'General Store Available' },
  { key: 'keyCardBuildingEntry', untranslatedLabel: 'Key Card Building Entry' },
  { key: 'lobbyBeverageAvailable', untranslatedLabel: 'Lobby Beverage Available' },
  { key: 'lobbyFoodAvailable', untranslatedLabel: 'Lobby Food Available' },
  { key: 'messengerCenter', untranslatedLabel: 'Messenger Center' },
  { key: 'parkingAvailable', untranslatedLabel: 'Parking Available' },
  { key: 'petsAllowed', untranslatedLabel: 'Pets Allowed' },
  { key: 'pharmacyAvailable', untranslatedLabel: 'Pharmacy Available' },
  { key: 'propertyManagerOnSite', untranslatedLabel: 'Property Manager On-Site' },
  { key: 'restaurantAvailable', untranslatedLabel: 'Restaurant Available' },
  { key: 'spaAvailable', untranslatedLabel: 'Spa Available' },
  { key: 'storageAvailable', untranslatedLabel: 'Storage Available' },
  { key: 'turnstiles', untranslatedLabel: 'Turnstiles' },
  { key: 'furnished', untranslatedLabel: 'Furnished' },
  { key: 'keyCardSpaceEntry', untranslatedLabel: 'Key Card Space Entry' },
  { key: 'passengerElevatorCount', untranslatedLabel: 'Passenger Elevator Count' },
  { key: 'serviceElevatorCount', untranslatedLabel: 'Service Elevator Count' },
];
const getAmenityObject = (label: AmenityDescription): AmenityObject => {
  return SEARCHABLE_AMENITIES.find(amenityObject => amenityObject.untranslatedLabel === label)!;
};

const AmenitiesFilter = ({ onChange, criteria, type }: Props) => {
  const [selectedAmenities, setSelectedAmenities] = useState(criteria.amenities());
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    setSelectedAmenities(criteria.amenities());
  }, [criteria]);

  const handleAmenitiesChange =
    (value: BuildingAmenityDescription | ListingAmenityDescription) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nowChecked = e.target.checked;

      if (nowChecked) {
        setSelectedAmenities([...selectedAmenities, value]);
        criteria.addValue('amenities', value);
      } else {
        setSelectedAmenities(selectedAmenities.filter(amenity => amenity !== value));
        criteria.removeValue('amenities', value);
      }

      const amenityLabels = criteria
        .amenities()
        .map(amenity => getAmenityObject(amenity as AmenityDescription))
        .map(amenityObject => amenityObject.key)
        .map(key => t(`filters:amenityLabels.${key}`));

      dispatch(actions.amenitiesFilterChanged(amenityLabels));
      onChange(criteria);
    };

  const sortedSearchableAmenities = SEARCHABLE_AMENITIES.sort((a, b) =>
    t(`filters:amenityLabels.${a.key}`).localeCompare(t(`filters:amenityLabels.${b.key}`)),
  );

  return (
    <ul className={s.filterList}>
      {sortedSearchableAmenities.map(amenityObject => (
        <li key={amenityObject.key}>
          <Checkbox
            className={s.checkbox}
            name={`${amenityObject.untranslatedLabel}-${type}`}
            checked={selectedAmenities.includes(amenityObject.untranslatedLabel)}
            onChange={handleAmenitiesChange(amenityObject.untranslatedLabel)}
          >
            {t(`filters:amenityLabels.${amenityObject.key}`)}
          </Checkbox>
        </li>
      ))}
    </ul>
  );
};

export default AmenitiesFilter;
